import React, { useEffect, useMemo, useState } from "react";

import {
	Box,
	DialogTitle,
	Dialog,
	DialogContent,
	Button,
	Alert,
	IconButton,
	CircularProgress,
	Switch
} from "@mui/material";
import CommentsInput from "../../Templates/WeddingTemplates/Components/CommentsInput";
import {
	INVITATION_PRICING_PLANS_ENUM,
	INVITATION_TYPES,
	Invitations,
	LangType
} from "../../../store/services/invitations/dto/invitations.dto";
import { CommentsInputWrapper } from "../../Templates/WeddingTemplates/Components/CommentsInput/styles";
import { LANG_AR, LANG_EN, RTL } from "../../Templates/WeddingTemplates/constants";
import langEN from "../../Templates/WeddingTemplates/lang.EN.json";
import langAR from "../../Templates/WeddingTemplates/lang.AR.json";
import { CameraAlt, Close } from "@mui/icons-material";
import UploadMemories from "../UploadMemories";
import { useAppSelector } from "../../../store";
import { getFullInvitationsState } from "../../../store/slices/invitations/invitations.slice";
import { addDays, isAfter } from "date-fns";
import { formatDate } from "../../../utils/formatDate";
import {
	useLazyGetWeddingInvitationCommentQuery,
	useLazyGetWeddingInvitationQuery
} from "../../../store/services/invitations/invitations.service";
import { getOrCreateAnonId } from "../../../utils/getOrCreateAnonId";
import { TemplateColorObject } from "../../../InvitationColorMatrix";
import { PRICING_PLAN_CONFIG } from "../../../constants";
interface IRsvpProps {
	open: boolean;
	onClose: () => void;
	canEdit: boolean;
	data: Invitations;
	password: string;
	id: string;
	colorObject?: TemplateColorObject;
	postComment?: (shouldSubscribeToNotifications: boolean) => void;
}

const Rsvp = ({ open, onClose, canEdit, data, password, id, postComment, colorObject }: IRsvpProps) => {
	const [upload, setUpload] = useState(false);
	const [isUploadedSuccessfully, setIsUploadedSuccessfully] = useState(false);
	const [shouldSubscribeToNotificationsState, setIsSubscribeToNotificationsState] = useState(false);
	const lang = data?.language === LANG_EN ? langEN : langAR;
	const { myComment, selectedInvitation } = useAppSelector(getFullInvitationsState);
	const anonymous_token = getOrCreateAnonId(id);
	const [fetchMyComment, { isLoading: isFetchingComment }] = useLazyGetWeddingInvitationCommentQuery();
	const [fetchInvitation, { isLoading: isFetchingInvitation }] = useLazyGetWeddingInvitationQuery();
	console.log(selectedInvitation);

	useEffect(() => {
		if (!myComment && id) {
			fetchMyComment({ id, password, anonymous_token });
		}
	}, [id, password, myComment]);

	useEffect(() => {
		if (id && !selectedInvitation) {
			fetchInvitation({ id });
		}
	}, [id, selectedInvitation]);

	const planConfig = useMemo(() => {
		return PRICING_PLAN_CONFIG[selectedInvitation?.selectedPricingPlan || 0];
	}, [selectedInvitation?.selectedPricingPlan]);

	const isFreePlan =
		(selectedInvitation?.selectedPricingPlan ?? INVITATION_PRICING_PLANS_ENUM.FREE) ===
		INVITATION_PRICING_PLANS_ENUM.FREE;

	const handleCloseUpload = () => {
		setUpload(false);
	};
	const postWeddingDate = useMemo(() => {
		if (!data) return new Date();
		return addDays(new Date(data.wedding_date), 2);
	}, [data]);

	const shouldDisableUpload = useMemo(() => {
		if (!postWeddingDate) return false;
		return isAfter(new Date(), postWeddingDate);
	}, [postWeddingDate]);

	const handleCommentRefetch = () => {
		const _anonymous_token = getOrCreateAnonId(id);
		fetchMyComment({ id, password, anonymous_token: _anonymous_token });
	};

	return (
		<Dialog
			open={open}
			onClose={() => {
				setIsUploadedSuccessfully(false);
				if (shouldSubscribeToNotificationsState) {
					postComment && postComment(shouldSubscribeToNotificationsState);
				}
				onClose();
			}}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			maxWidth={"xs"}
			fullWidth={true}
		>
			<DialogTitle id="alert-dialog-title" className={`${data?.language === LANG_AR && RTL}`}>
				{lang.RSVP}
			</DialogTitle>
			<Box
				display="flex"
				justifyContent="flex-end"
				style={
					data?.language === LANG_AR
						? { position: "absolute", left: "20px", top: "20px" }
						: { position: "absolute", right: "20px", top: "20px" }
				}
			>
				<IconButton
					onClick={() => {
						setIsUploadedSuccessfully(false);
						if (shouldSubscribeToNotificationsState) {
							postComment && postComment(shouldSubscribeToNotificationsState);
						}
						onClose();
					}}
				>
					<Close />
				</IconButton>
			</Box>
			<DialogContent>
				{isFetchingComment ? (
					<Box display={"flex"} justifyContent={"center"} alignItems={"center"} height={"20vh"}>
						<CircularProgress />
					</Box>
				) : (
					<CommentsInputWrapper>
						<Box>
							{shouldDisableUpload && (
								<Alert severity="info" className={data?.language === LANG_AR ? RTL : ""}>
									{lang.RSVP_CLOSED.replace("{{date}}", formatDate(postWeddingDate, data?.timezone))}
								</Alert>
							)}
							{!shouldDisableUpload && !isFreePlan && planConfig.memory_album_count > 0 && (
								<Alert severity="info" className={data?.language === LANG_AR ? RTL : ""}>
									{lang.UPLOAD_MEMORIES_INFO.replace("{{count}}", String(planConfig.memory_album_count)).replace(
										"{{date}}",
										formatDate(postWeddingDate, data?.timezone)
									)}
								</Alert>
							)}
						</Box>
						{isUploadedSuccessfully ? (
							<Box mt={1} mb={4}>
								<Alert severity={"success"}>Response posted successfully! 🎉</Alert>
							</Box>
						) : (
							<CommentsInput
								disable={canEdit || data?.type === INVITATION_TYPES.TEMPLATE || shouldDisableUpload}
								language={data?.language as LangType}
								invitationId={data?._id}
								myComment={myComment}
								refetchComment={handleCommentRefetch}
								postComment={(shouldSubscribeToNotifications: boolean) => {
									setIsUploadedSuccessfully(true);
									setIsSubscribeToNotificationsState(shouldSubscribeToNotifications);
								}}
								colorObject={colorObject}
							/>
						)}

						{data?.wedding_album && (
							<Button
								variant={"contained"}
								disabled={!myComment || !myComment?.is_email_verified || shouldDisableUpload}
								fullWidth
								className={`upload_memory_btn ${data?.language === LANG_AR ? RTL : ""}`}
								onClick={() => {
									setUpload(true);
								}}
							>
								<Box mx={1} display={"flex"} justifyContent={"center"}>
									<CameraAlt />
								</Box>
								{lang.UPLOAD_MEMORIES}
							</Button>
						)}
					</CommentsInputWrapper>
				)}

				{upload && (
					<UploadMemories
						id={data?._id}
						language={data?.language}
						open={true}
						onClose={handleCloseUpload}
						email={myComment?.email || ""}
						password={password}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default Rsvp;
